import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Briefcase,
  FileText,
  Receipt,
  CreditCard,
  PenTool,
  Calendar,
  MessageSquare,
  User,
  LogOut,
  Menu,
  X,
  Bell,
  Shield,
  History,
  HelpCircle
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useBranding } from '../hooks/useBranding'
import { useI18n } from '../hooks/useI18n'
import { Globe } from 'lucide-react'
import '../styles/PortalLayout.css'

const navItems = [
  { path: '/', icon: LayoutDashboard, labelKey: 'nav.dashboard', exact: true },
  { path: '/matters', icon: Briefcase, labelKey: 'nav.matters' },
  { path: '/documents', icon: FileText, labelKey: 'nav.documents' },
  { path: '/invoices', icon: Receipt, labelKey: 'nav.invoices' },
  { path: '/payments', icon: CreditCard, labelKey: 'nav.payments' },
  { path: '/expenses', icon: Receipt, labelKey: 'nav.expenses' },
  { path: '/trust', icon: Shield, labelKey: 'nav.trust' },
  { path: '/payment-history', icon: History, labelKey: 'nav.paymentHistory' },
  { path: '/appointments', icon: Calendar, labelKey: 'nav.appointments' },
  { path: '/messages', icon: MessageSquare, labelKey: 'nav.messages' },
  { path: '/profile', icon: User, labelKey: 'nav.profile' },
  { path: '/help', icon: HelpCircle, labelKey: 'nav.help' }
]

function PortalLayout() {
  const { client, logout } = useAuth()
  const { branding } = useBranding()
  const { t, locale, changeLocale, availableLocales } = useI18n()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="portal-layout">
      {/* Mobile header */}
      <header className="portal-mobile-header">
        <button
          className="menu-toggle"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={24} />
        </button>
        {branding.logo ? (
          <img src={branding.logo} alt={branding.firmName} className="portal-logo-img" />
        ) : (
          <span className="portal-logo">{branding.firmName}</span>
        )}
        <button
          className="notification-toggle"
          onClick={() => setShowNotifications(!showNotifications)}
          aria-label="Notifications"
        >
          <Bell size={24} />
        </button>
      </header>

      {/* Sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            className="sidebar-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`portal-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          {branding.logo ? (
            <img src={branding.logo} alt={branding.firmName} className="portal-logo-img" />
          ) : (
            <span className="portal-logo">{branding.firmName}</span>
          )}
          <button
            className="close-sidebar"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            {client?.firstName?.[0]}{client?.lastName?.[0]}
          </div>
          <div className="user-info">
            <span className="user-name">{client?.firstName} {client?.lastName}</span>
            <span className="user-email">{client?.email}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(({ path, icon: Icon, labelKey, exact }) => (
            <NavLink
              key={path}
              to={path}
              end={exact}
              className={({ isActive }) =>
                `nav-item ${isActive ? 'active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Icon size={20} />
              <span>{t(labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="language-switcher">
            <Globe size={16} />
            <select
              value={locale}
              onChange={(e) => changeLocale(e.target.value)}
              className="language-select"
            >
              {availableLocales.map(l => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
            <span>{t('nav.signOut')}</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="portal-main">
        <Outlet />
      </main>
    </div>
  )
}

export default PortalLayout
